import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { settingsAPI, paymentAPI, handleAPIError, authAPI } from '../services/api';
import { getPlanIdToNameMap } from '../config/stripe';
import { 
  User,
  CreditCard,
  Key,
  Shield,
  AlertTriangle,
  UserX,
  Trash2,
  CheckCircle,
  Calendar,
  DollarSign,
  Loader2,
  ArrowLeft,
  Undo2,
  Phone,
  Building,
  Mail
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const Settings = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, updateUser, isEmployee } = useAuth();

  // Profile state
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    phone_number: '',
    email: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileChanged, setProfileChanged] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Billing state  
  const [billingInfo, setBillingInfo] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [stripeBilling, setStripeBilling] = useState(null);

  // Account deletion state
  const [showDeletionForm, setShowDeletionForm] = useState(false);
  const [deletionForm, setDeletionForm] = useState({ confirmation_text: '', reason: '' });
  const [deletionLoading, setDeletionLoading] = useState(false);

  // Plan ID to Name mapping for human-readable plan names
  const [planIdToName, setPlanIdToName] = useState({});

  // Load plan ID to name mapping
  useEffect(() => {
    const loadPlanMapping = async () => {
      try {
        const mapping = await getPlanIdToNameMap();
        setPlanIdToName(mapping);
      } catch (error) {
        console.error('Error loading plan ID mapping:', error);
        // Fallback to empty object
        setPlanIdToName({});
      }
    };
    
    loadPlanMapping();
  }, []);

  useEffect(() => {
    if (user) {
      setProfile({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        company_name: user.company_name || '',
        phone_number: user.phone_number || '',
        email: user.email || ''
      });
      // Only update pending state from user if backend actually provided these fields
      if (typeof user.email_verification_pending === 'boolean') {
        setPendingEmailChange(user.email_verification_pending);
      }
      if (user.pending_email) {
        setPendingEmail(user.pending_email);
      }
    }
    
    loadBillingInfo();

    // Detect Stripe returns and refresh billing
    const params = new URLSearchParams(window.location.search);
    const billingReturn = params.get('billing_portal_return');
    const planChangeSuccess = params.get('plan_change_success');
    if (billingReturn === 'true' || planChangeSuccess === 'true') {
      // Refresh immediately, then clean URL
      loadBillingInfo();
      const clean = window.location.pathname;
      window.history.replaceState({}, document.title, clean);
    }

    // Short auto-refresh window to catch webhook updates
    const refreshWindowMs = 60000; // 1 minute
    const intervalMs = 10000; // every 10s
    const startedAt = Date.now();
    const interval = setInterval(() => {
      if (Date.now() - startedAt > refreshWindowMs) {
        clearInterval(interval);
        return;
      }
      loadBillingInfo();
    }, intervalMs);

    return () => clearInterval(interval);
  }, [user]);

  const loadBillingInfo = async () => {
    setBillingLoading(true);
    try {
      const [billingResponse, stripeResponse] = await Promise.allSettled([
        settingsAPI.getBillingInfo(),
        paymentAPI.getBillingStatus()
      ]);

      const base = billingResponse.status === 'fulfilled' ? billingResponse.value.data : null;
      const stripe = stripeResponse.status === 'fulfilled' ? stripeResponse.value.data : null;

      setBillingInfo(base);
      setStripeBilling(stripe);

      // If both present, merge chatbot billing details so UI updates instantly after webhook
      if (base && stripe && Array.isArray(base.chatbots) && Array.isArray(stripe.chatbots)) {
        const stripeById = new Map();
        stripe.chatbots.forEach(cb => {
          const key = cb.id || cb.chatbot_id;
          if (key) stripeById.set(key, cb);
        });
        const merged = base.chatbots.map(cb => {
          const key = cb.chatbot_id || cb.id;
          const stripeCb = key ? stripeById.get(key) : null;
          const selected_plan_id = stripeCb?.selected_plan_id ?? cb.selected_plan?.plan_id ?? cb.selected_plan_id;
          const selected_price_id = stripeCb?.selected_price_id ?? cb.selected_plan?.price_id ?? cb.selected_price_id;
          const subscription_status = stripeCb?.subscription_status ?? cb.selected_plan?.subscription_status ?? cb.subscription_status;
          const subscription_active = stripeCb?.subscription_active ?? cb.selected_plan?.subscription_active ?? cb.subscription_active;
          const subscription_trial_end = stripeCb?.subscription_trial_end ?? cb.selected_plan?.trial_end ?? cb.subscription_trial_end;
          const subscription_current_period_end = stripeCb?.subscription_current_period_end ?? cb.selected_plan?.current_period_end ?? cb.subscription_current_period_end;
          const plan_type = stripeCb?.plan_type ?? cb.selected_plan?.plan_type ?? cb.plan_type;
          return {
            ...cb,
            selected_plan: {
              ...(cb.selected_plan || {}),
              plan_id: selected_plan_id,
              price_id: selected_price_id,
              subscription_status,
              subscription_active,
              trial_end: subscription_trial_end,
              current_period_end: subscription_current_period_end,
              plan_type,
            },
            // Also hoist for easier access
            selected_plan_id,
            selected_price_id,
            subscription_status,
            subscription_active,
            subscription_trial_end,
            subscription_current_period_end,
            plan_type,
          };
        });
        setBillingInfo({ ...base, chatbots: merged });
      }
    } catch (error) {
      console.error('Error loading billing information:', error);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
    setProfileChanged(true);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);

    try {
      const response = await settingsAPI.updateProfile(profile);
      
      // Check if email was changed
      const emailChanged = profile.email !== user.email;
      
      if (emailChanged) {
        toast({
          title: "Profile Updated - Email Verification Required",
          description: "A 6-digit verification code has been sent to your new email address. Enter it below to complete the change.",
        });
        setPendingEmailChange(true);
        setPendingEmail(profile.email);
        
        // Update auth user with pending verification flags only when email changed
        updateUser({
          ...response.data,
          email_verification_pending: response.data.email_verification_pending ?? true,
          pending_email: response.data.pending_email || profile.email,
        });
      } else {
        toast({
          title: "Profile Updated",
          description: "Your profile information has been updated successfully.",
        });
        
        // Update auth user with response data but don't set email verification flags
        updateUser({
          ...response.data,
          email_verification_pending: response.data.email_verification_pending ?? false,
          pending_email: response.data.pending_email || null,
        });
      }
      setProfileChanged(false);
      
    } catch (error) {
      const errorMessage = handleAPIError(error, 'Failed to update profile');
      toast({
        title: "Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleRevertEmailChange = async () => {
    try {
      const response = await settingsAPI.revertEmailChange();
      toast({
        title: "Email Reverted",
        description: "Your email change has been cancelled and reverted to your original email.",
      });
      
      // Clear local state first
      setPendingEmailChange(false);
      setPendingEmail('');
      setVerificationCode('');
      
      // Refresh user data
      const userResponse = await settingsAPI.getProfile();
      updateUser({
        ...userResponse.data,
        email_verification_pending: false,
        pending_email: null
      });
      setProfile(prev => ({
        ...prev,
        email: userResponse.data.email
      }));
    } catch (error) {
      const errorMessage = handleAPIError(error, 'Failed to revert email change');
      toast({
        title: "Revert Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleAccountDeletion = async (e) => {
    e.preventDefault();
    setDeletionLoading(true);

    try {
      await settingsAPI.deleteAccount(deletionForm);
      toast({
        title: "Account Deletion Initiated",
        description: "Your account will be deleted in 30 days. Check your email for restoration instructions.",
      });
      
      // Redirect to login after successful deletion request
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      const errorMessage = handleAPIError(error, 'Failed to delete account');
      toast({
        title: "Deletion Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setDeletionLoading(false);
    }
  };

  const handleManageBillingForChatbot = async (chatbotId) => {
    try {
      toast({
        title: "Opening Billing Management",
        description: "Redirecting to manage billing for this chatbot...",
      });
      
      const response = await paymentAPI.createBillingPortalSession();
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No billing portal URL received');
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      const errorMessage = handleAPIError(error, 'Failed to open billing portal');
      toast({
        title: "Billing Portal Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <SEOHead 
        title="Settings" 
        description="Account settings and preferences"
        isIndexable={false}
      />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-white mr-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-white">Settings</h1>
          <p className="text-gray-400 mt-2">Manage your account settings and preferences</p>
        </div>

        <div className="space-y-8">
          {/* Profile Settings - Hidden for employees */}
          {!isEmployee() && (
            <Card className="bg-gray-900 border-gray-700">
            <CardHeader>
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-violet-400" />
                <div>
                  <CardTitle className="text-white">Profile Information</CardTitle>
                  <CardDescription>Update your personal information</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                {/* Email Change Notice */}
                {pendingEmailChange && (
                  <div className="p-4 bg-gray-800/80 border border-yellow-600/40 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-yellow-600/20 border border-yellow-600/40">
                        <Mail className="w-4 h-4 text-yellow-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-yellow-400 font-semibold">Verify your new email</h4>
                        <p className="text-yellow-200/90 text-sm mt-1">
                          Enter the 6-digit code we sent to <span className="font-semibold">{pendingEmail}</span>.
                        </p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <Input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            autoFocus
                            name="email_change_code"
                            aria-label="6-digit verification code"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            className="w-[160px] h-10 bg-gray-800 border border-yellow-600/60 text-white text-center text-xl tracking-[0.5em] placeholder:text-gray-400 focus-visible:ring-2 focus-visible:ring-yellow-500/70"
                            maxLength={6}
                          />
                          <Button type="button" onClick={async () => {
                            if (!pendingEmail || verificationCode.length !== 6) return;
                            setVerifying(true);
                            try {
                              await settingsAPI.verifyEmailChange(pendingEmail, verificationCode);
                              toast({ title: 'Email updated', description: 'Your email has been updated successfully.' });
                              
                              // Clear local state first
                              setPendingEmailChange(false);
                              setPendingEmail('');
                              setVerificationCode('');
                              
                              const userResponse = await settingsAPI.getProfile();
                              updateUser({
                                ...userResponse.data,
                                email_verification_pending: false,
                                pending_email: null
                              });
                              setProfile(prev => ({ ...prev, email: userResponse.data.email }));
                              
                              // Redirect to Dashboard after success with a one-time banner
                              setTimeout(() => {
                                navigate('/dashboard', { replace: true, state: { emailUpdated: true, newEmail: userResponse.data.email } });
                              }, 800);
                            } catch (error) {
                              const errorMessage = handleAPIError(error, 'Failed to verify email change');
                              toast({ title: 'Verification failed', description: errorMessage, variant: 'destructive' });
                            } finally {
                              setVerifying(false);
                            }
                          }} disabled={verifying || verificationCode.length !== 6} className="bg-yellow-600 hover:bg-yellow-700">
                            {verifying ? 'Verifying...' : 'Verify Email'}
                          </Button>
                          <Button type="button" variant="outline" onClick={async () => {
                            if (!pendingEmail) return;
                            setResendLoading(true);
                            try {
                              await settingsAPI.resendEmailChangeCode();
                              toast({ title: 'Code sent', description: `A new code was sent to ${pendingEmail}.` });
                            } catch (error) {
                              const errorMessage = handleAPIError(error, 'Failed to resend verification code');
                              toast({ title: 'Resend failed', description: errorMessage, variant: 'destructive' });
                            } finally {
                              setResendLoading(false);
                            }
                          }} disabled={resendLoading} className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-white" variant="outline">
                            {resendLoading ? 'Resending...' : 'Resend Code'}
                          </Button>
                          <Button
                            type="button"
                            onClick={handleRevertEmailChange}
                            variant="outline"
                            className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-white"
                          >
                            <Undo2 className="w-4 h-4 mr-1" />
                            Revert
                          </Button>
                        </div>
                        <p className="text-xs text-yellow-400 mt-2">Code expires in 30 minutes.</p>
                      </div>
                    </div>
                  </div>
                )}

                {!pendingEmailChange && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="first_name" className="text-gray-300">First Name</Label>
                        <Input
                          id="first_name"
                          value={profile.first_name}
                          onChange={(e) => handleProfileChange('first_name', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white"
                          placeholder="Enter your first name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="last_name" className="text-gray-300">Last Name</Label>
                        <Input
                          id="last_name"
                          value={profile.last_name}
                          onChange={(e) => handleProfileChange('last_name', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white"
                          placeholder="Enter your last name"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="company_name" className="text-gray-300">Company Name</Label>
                      <Input
                        id="company_name"
                        value={profile.company_name}
                        onChange={(e) => handleProfileChange('company_name', e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white"
                        placeholder="Enter your company name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone_number" className="text-gray-300">Phone Number</Label>
                      <Input
                        id="phone_number"
                        value={profile.phone_number}
                        onChange={(e) => handleProfileChange('phone_number', e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white"
                        placeholder="Enter your phone number"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-gray-300">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profile.email}
                        onChange={(e) => handleProfileChange('email', e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white"
                        placeholder="Enter your email address"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Changing your email will require verification. You can revert before verification is complete.
                      </p>
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit" disabled={profileLoading || !profileChanged} className="bg-violet-600 hover:bg-violet-700">
                        {profileLoading ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </>
                )}


              </form>
            </CardContent>
          </Card>
          )}

          {/* Billing & Subscription - Hidden for employees */}
          {!isEmployee() && (
            <Card className="bg-gray-900 border-gray-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <CreditCard className="w-5 h-5 text-violet-400" />
                  <div>
                    <CardTitle className="text-white">Billing & Chatbots</CardTitle>
                    <CardDescription>Manage your billing and subscription information</CardDescription>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={loadBillingInfo}
                    disabled={billingLoading}
                    size="sm"
                    variant="outline"
                    className="border-violet-500 text-violet-400 hover:bg-violet-500 hover:text-white"
                  >
                    {billingLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <DollarSign className="w-4 h-4 mr-2" />
                    )}
                    Refresh Billing
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {billingLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-violet-400 mr-2" />
                  <span className="text-gray-400">Loading billing information...</span>
                </div>
              ) : billingInfo ? (
                <>
                  {/* Payment Summary - Fixed with chatbot counts */}
                  <div className="p-4 bg-gray-800 rounded-lg">
                    <h3 className="text-lg font-semibold text-white mb-4">Payment Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-gray-700 rounded-lg">
                        <DollarSign className="w-5 h-5 text-green-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Chatbots with Deposits</p>
                        <p className="text-xl font-bold text-green-400">
                          {billingInfo.chatbots?.filter(bot => bot.deposit_paid)?.length || 0}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-gray-700 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Final Payments Completed</p>
                        <p className="text-xl font-bold text-blue-400">
                          {billingInfo.chatbots?.filter(bot => bot.final_payment_paid)?.length || 0}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-gray-700 rounded-lg">
                        <Calendar className="w-5 h-5 text-violet-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Active Subscriptions</p>
                        <p className="text-xl font-bold text-violet-400">
                          {billingInfo.chatbots?.filter(bot => bot.selected_plan?.subscription_active)?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Chatbots Billing - Enhanced with human-readable plan names and manage billing */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Your Chatbots</h3>
                    {billingInfo.chatbots && billingInfo.chatbots.length > 0 ? (
                      <div className="space-y-4">
                        {billingInfo.chatbots.map((chatbot, index) => {
                          const stripeCb = stripeBilling?.chatbots?.find(c => c.chatbot_id === chatbot.chatbot_id);
                          const verified = stripeCb?.stripe_verification?.verified_payments || [];
                          const verifiedTotal = stripeCb?.stripe_verification?.total_verified_amount || 0;
                          const planName = planIdToName[chatbot.selected_plan?.plan_id] || chatbot.selected_plan?.plan_id;
                          
                          return (
                            <div key={chatbot.chatbot_id} className="p-4 border border-gray-600 rounded-lg bg-gray-800">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <h4 className="font-semibold text-white">{chatbot.chatbot_name}</h4>
                                  {chatbot.company_name && (
                                    <p className="text-sm text-gray-400">{chatbot.company_name}</p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Badge className={`${
                                    chatbot.status === 'done' ? 'bg-green-600' :
                                    chatbot.status === 'deploy' ? 'bg-blue-600' :
                                    chatbot.status === 'testing' ? 'bg-yellow-600' :
                                    'bg-gray-600'
                                  } text-white`}>
                                    {chatbot.status}
                                  </Badge>
                                  {/* SINGLE MANAGE BILLING BUTTON PER CHATBOT */}
                                  {chatbot.final_payment_paid && (
                                    <Button
                                      onClick={() => handleManageBillingForChatbot(chatbot.chatbot_id)}
                                      size="sm"
                                      className="bg-violet-600 hover:bg-violet-700"
                                    >
                                      <CreditCard className="w-4 h-4 mr-1" />
                                      Manage Billing
                                    </Button>
                                  )}
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm justify-items-center">
                                <div className="text-center">
                                  <p className="text-gray-400">Deposit Status</p>
                                  <div className="flex items-center justify-center mt-1">
                                    {chatbot.deposit_paid ? (
                                      <>
                                        <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                                        <span className="text-green-400">${chatbot.deposit_amount || 200} Paid</span>
                                      </>
                                    ) : (
                                      <>
                                        <AlertTriangle className="w-4 h-4 text-yellow-400 mr-2" />
                                        <span className="text-yellow-400">Pending</span>
                                      </>
                                    )}
                                  </div>
                                  {chatbot.deposit_paid_at && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {new Date(chatbot.deposit_paid_at).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                                
                                <div className="text-center">
                                  <p className="text-gray-400">Final Payment</p>
                                  <div className="flex items-center justify-center mt-1">
                                    {chatbot.final_payment_paid ? (
                                      <>
                                        <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                                        <span className="text-green-400">${chatbot.final_payment_amount || 400} Paid</span>
                                      </>
                                    ) : (
                                      <>
                                        <AlertTriangle className="w-4 h-4 text-yellow-400 mr-2" />
                                        <span className="text-yellow-400">Pending</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="text-center">
                                  <p className="text-gray-400">Plan</p>
                                  <div className="flex items-center justify-center mt-1">
                                    {planName && planName !== chatbot.selected_plan?.plan_id ? (
                                      <>
                                        <CheckCircle className="w-4 h-4 text-blue-400 mr-2" />
                                        <span className="text-blue-400">{planName}</span>
                                      </>
                                    ) : chatbot.selected_plan?.plan_id ? (
                                      <>
                                        <CheckCircle className="w-4 h-4 text-blue-400 mr-2" />
                                        <span className="text-blue-400">{chatbot.selected_plan.plan_id}</span>
                                      </>
                                    ) : (
                                      <>
                                        <AlertTriangle className="w-4 h-4 text-gray-400 mr-2" />
                                        <span className="text-gray-400">Not Selected</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {chatbot.created_at && (
                                <div className="mt-3 pt-3 border-t border-gray-700">
                                  <p className="text-xs text-gray-500">
                                    Created: {new Date(chatbot.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400 mb-2">No chatbots found</p>
                        <p className="text-sm text-gray-500">Create your first chatbot to see billing information</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">No billing information available</p>
                  <p className="text-sm text-gray-500">Create a chatbot to start your billing journey</p>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Security Settings */}
          <Card className="bg-gray-900 border-gray-700">
            <CardHeader>
              <div className="flex items-center space-x-3">
                <Shield className="w-5 h-5 text-violet-400" />
                <div>
                  <CardTitle className="text-white">Security</CardTitle>
                  <CardDescription>Password and security settings</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate('/change-password')}
                variant="outline"
                className="border-violet-500 text-violet-400 hover:bg-violet-500 hover:text-white"
              >
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </CardContent>
          </Card>

          {/* Danger Zone - Hidden for employees */}
          {!isEmployee() && (
            <Card className="bg-gray-900 border-red-600">
            <CardHeader>
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <div>
                  <CardTitle className="text-red-400">Danger Zone</CardTitle>
                  <CardDescription>Permanently delete your account and data</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!showDeletionForm ? (
                <div className="flex items-center justify-between p-4 border border-red-600 rounded-lg bg-red-950/20">
                  <div>
                    <h3 className="text-white font-medium">Delete Account</h3>
                    <p className="text-gray-400 text-sm">
                      Your account will be deactivated for 30 days before permanent deletion
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowDeletionForm(true)}
                    variant="destructive"
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <UserX className="w-4 h-4 mr-2" />
                    Delete Account
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleAccountDeletion} className="space-y-4">
                  <div className="p-4 border border-red-600 rounded-lg bg-red-950/20">
                    <div className="mb-4">
                      <h3 className="text-white font-medium mb-2">Confirm Account Deletion</h3>
                      <p className="text-gray-400 text-sm mb-4">
                        This action cannot be undone. Your account will be scheduled for deletion in 30 days.
                        You can restore it within this period by using the restoration code sent to your email.
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="confirmation_text" className="text-gray-300">
                            Type "DELETE" to confirm
                          </Label>
                          <Input
                            id="confirmation_text"
                            value={deletionForm.confirmation_text}
                            onChange={(e) => setDeletionForm(prev => ({...prev, confirmation_text: e.target.value}))}
                            placeholder="DELETE"
                            className="bg-gray-800 border-red-600 text-white"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="reason" className="text-gray-300">
                            Reason for deletion (optional)
                          </Label>
                          <Input
                            id="reason"
                            value={deletionForm.reason}
                            onChange={(e) => setDeletionForm(prev => ({...prev, reason: e.target.value}))}
                            placeholder="Tell us why you're leaving..."
                            className="bg-gray-800 border-gray-600 text-white"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-2">
                      <Button
                        type="button"
                        onClick={() => {
                          setShowDeletionForm(false);
                          setDeletionForm({ confirmation_text: '', reason: '' });
                        }}
                        variant="outline"
                        className="border-gray-600 text-gray-400 hover:bg-gray-700"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={deletionLoading || deletionForm.confirmation_text.toUpperCase() !== 'DELETE'}
                        variant="destructive"
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {deletionLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Account
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;